import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import {
  getCmiDigestWatchlistRunCommunities,
  getCmiDigestWatchlistRunOverview,
  getCmiDigestWatchlistRuns,
} from 'apis/CmiDigestWatchlistAPI';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { renderBoldMarkup } from 'helpers/renderBoldMarkup';
import { CMI_DIGEST_WATCHLIST_TYPE, CmiDigestWatchlistType } from 'types/cmiDigestWatchlist';

import CommunitiesTable from './CommunitiesTable';
import CommunitiesToWatchFilters from './CommunitiesToWatchFilters';
import CommunitiesToWatchHeader from './CommunitiesToWatchHeader';
import DigestSelector from './DigestSelector';
import { isValidTypeFilter } from './helpers';
import QuickFilterCards from './QuickFilterCards';

const Container = styled.div`
  width: 100%;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 200px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 auto;
  padding: 10px 30px 30px 30px;
`;

const AiSummaryCard = styled.div``;

const AiSummaryTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-family: var(--font-bold);
`;

const AiSummaryDivider = styled.div`
  height: 1px;
  margin: 10px 0 10px;
  background-image: linear-gradient(to left, var(--flamingo-pink), var(--competitor-purple));
`;

const AiSummaryList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style-type: '· ';
  list-style-position: inside;
`;

const AiSummaryLine = styled.li`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 6px;

  strong {
    color: var(--text-primary);
    font-family: var(--font-bold);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CommunitiesToWatch = () => {
  const [searchParams] = useSearchParams();

  const [selectedRunId, setSelectedRunId] = useState<number | null>(() => {
    const runIdParam = searchParams.get('runId');
    return runIdParam ? Number(runIdParam) : null;
  });
  const [typeFilter, setTypeFilter] = useState<CmiDigestWatchlistType>(() => {
    const typeParam = searchParams.get('type');
    return isValidTypeFilter(typeParam) ? typeParam : CMI_DIGEST_WATCHLIST_TYPE.NEEDS_ATTENTION;
  });
  const [search, setSearch] = useState('');

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ['cmi-digest-watchlist-runs'],
    queryFn: getCmiDigestWatchlistRuns,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (selectedRunId || !runs || runs.length === 0) return;
    setSelectedRunId(runs[0].id);
  }, [runs, selectedRunId]);

  const handleReset = () => {
    setTypeFilter(CMI_DIGEST_WATCHLIST_TYPE.NEEDS_ATTENTION);
    setSearch('');
  };

  const {
    data: overview,
    isLoading: overviewLoading,
    isFetching: overviewFetching,
  } = useQuery({
    queryKey: ['cmi-digest-watchlist-overview', selectedRunId],
    queryFn: () => getCmiDigestWatchlistRunOverview(selectedRunId ?? -1),
    enabled: Boolean(selectedRunId),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: rows, isLoading: rowsLoading } = useQuery({
    queryKey: ['cmi-digest-watchlist-communities', selectedRunId, typeFilter, search],
    queryFn: () => getCmiDigestWatchlistRunCommunities(selectedRunId ?? -1, { type: typeFilter, search }),
    enabled: Boolean(selectedRunId),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  // keepPreviousData above avoids unmounting the selector/filters between requests, but that also
  // means switching to a different digest period would otherwise keep showing the PREVIOUS period's
  // summary/table underneath the newly selected one until the new data lands - isFetching (not just
  // isLoading, which keepPreviousData suppresses after the very first load) catches that window.
  const isLoading = runsLoading || (Boolean(selectedRunId) && (overviewLoading || overviewFetching));

  return (
    <Container>
      <CommunitiesToWatchHeader />
      {isLoading && (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      )}
      {!runsLoading && (!runs || runs.length === 0) && (
        <ContentContainer>No digests have been generated yet.</ContentContainer>
      )}
      {!isLoading && runs && runs.length > 0 && selectedRunId && (
        <ContentContainer>
          <DigestSelector runs={runs} selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
          {overview && overview.overall_communities_summary_lines.length > 0 && (
            <AiSummaryCard>
              <AiSummaryTitle>
                <img src="/icons/ai-blob.svg" alt="" width={20} height={20} />
                AI Summary
              </AiSummaryTitle>
              <AiSummaryDivider />
              <AiSummaryList>
                {overview.overall_communities_summary_lines.map((line, index) => (
                  <AiSummaryLine key={index}>{renderBoldMarkup(line)}</AiSummaryLine>
                ))}
              </AiSummaryList>
            </AiSummaryCard>
          )}
          <QuickFilterCards overview={overview} typeFilter={typeFilter} onTypeFilterChange={setTypeFilter} />
          <CommunitiesToWatchFilters
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            onSearchChange={setSearch}
            onReset={handleReset}
          />
          <CommunitiesTable rows={rows ?? []} loading={rowsLoading} runId={selectedRunId} />
        </ContentContainer>
      )}
    </Container>
  );
};

export default CommunitiesToWatch;
